<template>
  <TemplateBase>
    <main class="col-span-full row-start-2 mb-6">
      <!-- Breadcrumbs -->
      <OrganismNavigationBreadcrumb class="mx-auto p-4 md:container md:mb-8 md:pb-0 md:pt-10 xl:mb-20" :path="breadcrumbs" />

      <section class="md:container md:grid md:gap-x-6 md:grid-cols-12 md:mx-auto md:px-5 xl:gap-x-8">
        <section class="relative z-10 flex flex-row items-center mb-4 px-4 md:col-span-full md:col-start-1 md:row-start-1 md:px-0 xl:mb-8">
          <figure class="flex items-center justify-center mr-2 w-16 h-16 bg-white rounded-xl sm:mr-4 md:mr-6 md:w-20 md:h-20">
            <AtomIcon :icon="get(page, 'icon.slug')" class="w-10 h-10 text-app-green md:w-12 md:h-12" />
          </figure>
          <h1 class="text-black text-lg font-bold leading-tight uppercase sm:text-xl md:text-3xl md:leading-8">
            {{ t_('title', { title: page.title }) }}
          </h1>
        </section>

        <section class="items-center mb-3 mb:mb-5 w-full md:flex md:hidden md:col-span-full md:col-start-1 md:border-t-2 md:border-solid md:border-white">
          <div class="px-4 2xl:px-56 py-3 border-b-2 border-t-2 border-solid border-white md:mr-8 md:p-0 md:w-full md:border-0">
            <AtomButton button-type="filter" class="w-full sm:w-1/4" @click.prevent="toggleFilters">
              <AtomIcon icon="app-filter" class="mr-6 w-5 h-5" />
              Filters
            </AtomButton>
          </div>
        </section>

        <!-- filters mobile -->
        <section v-if="filtersOpened" class="filters filters--opened fixed top-0 w-full h-screen bg-app-green-100 overflow-auto overscroll-contain md:hidden">
          <div :class="{ 'h-screen': filtersOpened, 'h-0': !filtersOpened }">
            <header class="fixed z-10 top-0 flex items-center justify-between px-4 py-3 w-full bg-white border-b-2 border-solid border-app-green-100">
              <div class="flex items-center ml-4">
                <AtomIcon icon="app-filter" class="mr-4 w-5 h-5 text-app-green" />
                <h2 class="text-lg font-bold leading-none uppercase">Filters</h2>
              </div>
              <AtomButton button-type="tertiary" :full-width="false" @click.prevent="toggleFilters">
                <AtomIcon icon="x" class="w-4 h-4 text-white" />
              </AtomButton>
            </header>

            <!-- SEARCH POSTAL -->
            <ul>
              <li>
                <button class="w-full flex items-center justify-between px-8 py-3 bg-white cursor-pointer" @click.prevent="mobileSearchFilterOpened = !mobileSearchFilterOpened">
                  <h3 class="text-base font-bold leading-none">{{ t_('search-nearby') }}</h3>
                  <AtomIcon icon="chevron-up" class="w-4 h-4 text-app-green transform transition duration-200" :class="{ 'rotate-180': mobileSearchFilterOpened }" />
                </button>
                <SmoothReflow class="m-0 p-0 overflow-hidden transition-all duration-500 ease-in-out">
                  <div :class="{ 'h-auto': mobileSearchFilterOpened, 'h-0': !mobileSearchFilterOpened }">
                    <OrganismLocationSearchInput
                      class="px-8 py-4 md:hidden"
                      :location="orderByLocation.location"
                      :distance="orderByLocation.distance"
                      :city="orderByLocation.city"
                      :errors="orderByLocation.errors"
                      :class="{ block: mobileSearchFilterOpened, hidden: !mobileSearchFilterOpened }"
                      @location="setLocation"
                      @distance="setDistance"
                    />
                  </div>
                </SmoothReflow>
              </li>
            </ul>

            <!-- ANIMAL FACETS -->
            <ul v-if="enabledFacetConfigs.length">
              <template v-for="config of enabledFacetConfigs">
                <template v-if="facets[config.handle] && facets[config.handle].length">
                  <!-- todo: currently only support 'checkboxes' type, we will need to expand on this -->
                  <li :key="config.handle" class="border-t border-solid border-app-green-200">
                    <button
                      class="w-full flex items-center justify-between px-8 py-3 bg-white cursor-pointer"
                      @click.prevent="facetsOpened[config.handle] = !facetsOpened[config.handle]"
                    >
                      <h3 class="text-base font-bold leading-none">{{ config.label }}</h3>
                      <AtomIcon icon="chevron-up" class="w-4 h-4 text-app-green transform transition duration-200" :class="{ 'rotate-180': facetsOpened[config.handle] }" />
                    </button>
                    <SmoothReflow class="m-0 p-0 overflow-hidden transition-all duration-500 ease-in-out">
                      <div :class="{ 'h-auto py-2': facetsOpened[config.handle], 'h-0': !facetsOpened[config.handle] }">
                        <FormKit
                          v-model="input[config.handle]"
                          class="pl-8 pr-4 py-4 text-sm leading-none xl:text-base"
                          type="checkbox"
                          :options="facets[config.handle].map((facet) => ({ label: `${facetValueToLabel(config, facet.value)} (${facet.count})`, value: facet.value }))"
                          :label="false"
                          :class="{ block: facetsOpened[config.handle], hidden: !facetsOpened[config.handle] }"
                        />
                      </div>
                    </SmoothReflow>
                  </li>
                </template>
              </template>
            </ul>

            <!-- SHOW RESULTS BUTTON -->
            <div class="px-8 py-4 bg-white border-t border-solid border-app-green-200 pb-32">
              <AtomButton button-type="primary" @click.prevent="toggleFilters">{{ tc_('show-results', count) }}</AtomButton>
            </div>
          </div>
        </section>

        <section class="2xl:col-span-2 h-fit col-start-1 mb-4 md:col-span-3 md:bg-white p-3 md:p-5 rounded-md">
          <OrganismLocationSearchInput
            class="mx-4 md:mx-0"
            :location="orderByLocation.location"
            :distance="orderByLocation.distance"
            :city="orderByLocation.city"
            :errors="orderByLocation.errors"
            @location="setLocation"
            @distance="setDistance"
          />

          <!-- checkbox -->
          <FormKit
            v-if="domain === 'ikZoekBaas'"
            v-model="onlyWhenAvailable"
            tabindex="0"
            type="checkbox"
            name="filter"
            label="Toon alleen beschikbare dieren"
            class="c-animal-overview-formulate-container-checkbox px-4 leading-snug md:px-0 mt-4"
            label-class="underline !font-semibold text-black xl:!text-sm c-animal-overview-label-available-animals"
            decorator-class="!mr-2"
          />

          <div class="px-4">
            <MoleculeResultsAndOrderBy
              v-if="page && page.title"
              :count="count"
              :is-ready="isReady"
              :title="page.title.toLowerCase()"
              :order-by-location="orderByLocation.status === 'ok'"
              :order-by-date="orderByDate"
              class="md:hidden"
              @value-update="updateOrderByDate"
            />
          </div>

          <!-- filters desktop -->
          <section class="hidden pt-2 bg-app-white md:block">
            <div>
              <!-- ANIMAL FACETS -->
              <ul v-if="enabledFacetConfigs.length">
                <template v-for="(config, index) in enabledFacetConfigs">
                  <template v-if="facets[config.handle] && facets[config.handle].length">
                    <li
                      :key="config.handle"
                      class="mb-3 border-b border-solid border-app-green border-opacity-20"
                      :class="{ 'border-b-0': index === enabledFacetConfigs.length - 1 }"
                    >
                      <button class="w-full flex items-center justify-between pb-3 cursor-pointer" @click.prevent="facetsOpened[config.handle] = !facetsOpened[config.handle]">
                        <h3 class="text-black text-sm font-bold leading-none xl:text-base">{{ config.label }}</h3>
                        <AtomIcon
                          icon="chevron-up"
                          class="shrink-0 ml-2 w-4 h-4 text-app-green transform transition duration-200"
                          :class="{ 'rotate-180': facetsOpened[config.handle] }"
                        />
                      </button>
                      <SmoothReflow class="m-0 p-0 overflow-hidden transition-all duration-500 ease-in-out facet-group-smooth-reflow-container">
                        <div :class="{ 'h-auto pb-3': facetsOpened[config.handle], 'h-0': !facetsOpened[config.handle] }">
                          <FormKit
                            v-model="input[config.handle]"
                            class="pb-3 text-sm leading-none xl:text-base"
                            type="checkbox"
                            tabindex="0"
                            :options="facets[config.handle].map((facet) => ({ label: `${facetValueToLabel(config, facet.value)} (${facet.count})`, value: facet.value }))"
                            :label="false"
                            :class="{ block: facetsOpened[config.handle], hidden: !facetsOpened[config.handle] }"
                          />
                        </div>
                      </SmoothReflow>
                    </li>
                  </template>
                </template>
              </ul>
            </div>
          </section>
        </section>

        <section class="2xl:col-span-10 md:col-span-9 md:px-0 md:border-0">
          <!-- filtered by -->
          <div v-if="activeFacets.length > 0 || orderByLocation.status === 'ok'" class="mb-4 mt-4 px-4 md:mb-2 md:mt-0 md:px-0 md:grid md:grid-cols-12">
            <ul class="flex flex-wrap items-center mb-2 md:col-span-8">
              <li v-for="(activeFacetGroup, index) in activeFacets" :key="index" class="flex flex-wrap items-center sm:mr-3">
                <h4 class="hidden text-black mb-1 mr-1 text-sm leading-none sm:block">{{ activeFacetGroup.label }}</h4>
                <div v-for="(activeFacet, activeIndex) in activeFacetGroup.active" :key="activeIndex" class="mb-1 mr-1">
                  <MoleculeFilter :selected="true" :text="activeFacet.label" @click.prevent="deactivateFacet(activeFacet)" />
                </div>
              </li>
            </ul>

            <client-only v-if="page.site.handle == 'ikZoekBaas'">
              <div class="md:col-span-4 md:ml-auto">
                <button v-if="hasSearchProfile" class="flex items-center text-sm bg-app-lime text-black p-1.5 px-3 rounded-md">
                  <AtomIcon icon="check" class="shrink-0 mr-2 w-4 h-4 text-black" />
                  <span>
                    <AtomLink class="border-b-2 hover:border-app-green border-transparent cursor-pointer" :url="{ name: 'account-detail' }">
                      {{ t_('show-search-profiles-in-account') }}
                    </AtomLink>
                  </span>
                </button>
                <button v-if="!hasSearchProfile" class="flex items-center text-sm bg-black text-white p-1.5 px-3 rounded-md">
                  <AtomIcon icon="bookmark" class="shrink-0 mr-3 w-4 h-4 text-white" />
                  <span class="cursor-pointer" @click="toggleSearchProfile">
                    {{ t_('save-search-profile') }}
                  </span>
                </button>
              </div>
            </client-only>
          </div>

          <MoleculeResultsAndOrderBy
            v-if="page && page.title"
            :count="count"
            :is-ready="isReady"
            :title="page.title.toLowerCase()"
            :order-by-location="orderByLocation.status === 'ok'"
            :order-by-date="orderByDate"
            class="hidden md:flex md:col-span-4"
            @value-update="updateOrderByDate"
          />

          <!-- results -->
          <section class="grid gap-6 grid-cols-1 px-4 md:grid-cols-12 md:px-0 xl:gap-8" :class="{ 'opacity-50': !isReady }">
            <div v-for="entry in entries" :key="entry.id" class="2xl:col-span-3 md:col-span-6 lg:col-span-4">
              <OrganismLoaderFromAnimalId :animal-id="entry.id" class="h-full">
                <template #loading><MoleculeAnimalCardV2Preloader /></template>
                <template #default="{ animal, shelter }"><MoleculeAnimalCardV2 :animal="animal" :shelter="shelter" /></template>
              </OrganismLoaderFromAnimalId>
            </div>
            <p v-show="!overlayStore.loader && !entries.length && isReady" class="col-span-full mt-5 text-sm font-semibold leading-snug lg:col-span-9 text-black">
              {{ t_('no-results') }}
            </p>
            <div v-show="!entries.length && !isReady" class="pl-2 pt-2 md:pt-0">
              <div class="inline-block scale-x-[-1]">
                <AtomIcon :icon="get(page, 'icon.slug')" class="w-10 h-10 text-app-green md:w-12 md:h-12 animate-bounce" />
              </div>
            </div>
          </section>
        </section>
      </section>

      <OrganismPagination :count="count" :limit="limit" :page="pageNumber" class="mb-6 mt-6" @click="({ page }) => (pageNumber = page)" />
      <OrganismCallToActionList v-if="ctas" :ctas="ctas" class="px-4 py-4" />
    </main>
  </TemplateBase>
</template>

<script setup lang="ts">
import get from 'lodash/get'
import sortBy from 'lodash/sortBy'
import { unref } from 'vue'
import { setupTranslationPrefixer, toStringArray } from '~/composables/util'
import { getPropertiesString } from '~/assets/queries'
import { setupDynamicUrl } from '@/composables/setup-dynamic-url'
import { setupAnimalFacetConfigs, setupFacetValueToLabel } from '@/assets/animal-attributes'
import { setupSiteConfig } from '~/composables/craft'
import { fallbackDistance, useLocationSearchStore } from '~/store/location-search'
import { useGraphqlFetch } from '~/composables/craft-fetch'
import { useSearchProfilesStore } from '~/store/searchProfiles'
import { useCacheStore } from '~/store/cache'
import { useOverlayStore } from '~/store/overlay'
import { useCtaStore } from '~/store/call-to-action'

interface Props {
  breadcrumbs: any[]
  page: Record<string, any>
  limit?: number
}

const props = withDefaults(defineProps<Props>(), {
  limit: 15,
})

const route = useRoute()
const cacheStore = useCacheStore()
const locationSearchStore = useLocationSearchStore()
const searchProfilesStore = useSearchProfilesStore()
const overlayStore = useOverlayStore()
const ctaStore = useCtaStore()
const graphqlFetch = useGraphqlFetch()

// Consider moving this code to deduplicate it (currently used in both the AnimalOverview and AnimalDetail templates)
function setupFilters(page) {
  // Every animal has a category, i.e. 'honden', 'katten', etc.  This corresponds to hardcoded 'slugs' in AnimalCategoryTrait
  const animalCategory = page.animalCategory

  // `animalType`
  // Every animal can be 'available', 'found', or 'missing'.  We show animals of this type depending on the url, i.e.
  // ikzoekbaas.dierenbescherming.nl/zoek-asieldieren    --> 'available'
  // mijndieriszoek.dierenbescherming.nl/gevonden-dieren --> 'found'
  // mijndieriszoek.dierenbescherming.nl/vermiste-dieren --> 'missing'

  switch (page.site?.handle) {
    case 'mijnDierIsZoek':
      if (/vermiste-dieren/i.test(page.url)) {
        return { animalCategory, animalType: 'missing' }
      }

      // Fallback assume it is for 'gevonden-dieren'
      return { animalCategory, animalType: 'found' }

    case 'ikZoekBaas':
    default:
      return { animalCategory, animalType: 'available' }
  }
}

function setupOnlyWhenAvailable() {
  const onlyWhenAvailable = ref(route.query.animalAvailability === 'available')
  return { onlyWhenAvailable }
}

function setupFacets(onlyWhenAvailable: any, enabledFacetConfigs: any[], animalCategory: any, animalType: any) {
  // Always supply a site attribute, though it might be overwritten though the `enabledFacetConfigs`.
  // Hence start with `site: []`, and follow with `...Object.fromEntries(enabledFacetConfigs`
  const variables = computed(() => ({
    site: [],
    ...Object.fromEntries(enabledFacetConfigs.filter((config) => config.handle === 'site').map((config) => [config.handle, toStringArray(route.query[config.uriName])])),
    animalAvailability: onlyWhenAvailable.value ? ['available'] : [],
    animalCategory,
    animalType,
  }))

  const facets = ref({})
  const update = async (variables_: any) => {
    const key = `animal-overview;facets;${JSON.stringify(variables_)}`
    const ttl = 3
    const fetcher = async () => {
      const { elasticSearch } = await graphqlFetch({
        token: 'elasticsearch',
        query: `
          query setupFacets($animalCategory: [String], $animalType: [String], $animalAvailability: [String], $site: [String]) {
            elasticSearch(section: "animal", site: $site, animalCategoryFacet: $animalCategory, animalTypeFacet: $animalType, animalAvailabilityFacet: $animalAvailability) {
              facets(limit:100) {
                ${enabledFacetConfigs.map((facet) => `${facet.handle}${getPropertiesString(facet.facetArguments)} {value count}`).join(`\n`)}
              }
            }
          }`,
        variables: variables_,
        params: Object.fromEntries(Object.entries({ animalCategory: variables_.animalCategory, animalType: variables_.animalType }).filter(([_key, value]) => !!value)),
      })

      const facets = elasticSearch.facets

      // Provide custom order for the birthdate facet
      facets.birthdate = sortBy(elasticSearch.facets?.birthdate || [], 'value')

      // todo: move this to elasticsearch... but do it here for now
      facets.animalSubCategoryBirds = (elasticSearch.facets?.animalSubCategoryBirds || []).filter((facet) => !!facet.value)
      facets.animalSubCategoryCats = (elasticSearch.facets?.animalSubCategoryCats || []).filter((facet) => !!facet.value)
      facets.animalSubCategoryDogs = (elasticSearch.facets?.animalSubCategoryDogs || []).filter((facet) => !!facet.value)
      facets.animalSubCategoryExLaboratoryAnimals = (elasticSearch.facets?.animalSubCategoryExLaboratoryAnimals || []).filter((facet) => !!facet.value)
      facets.animalSubCategoryOther = (elasticSearch.facets?.animalSubCategoryOther || []).filter((facet) => !!facet.value)
      facets.animalSubCategoryRabbitsAndRodents = (elasticSearch.facets?.animalSubCategoryRabbitsAndRodents || []).filter((facet) => !!facet.value)

      return facets
    }
    facets.value = await cacheStore.fetch({ key, ttl, fetcher, fallback: {} })
  }

  onMounted(async () => await update(variables.value))
  watch(variables, update)

  return { facets }
}

async function setupLocation() {
  const orderByLocation = ref({
    distance: route.query.distance || fallbackDistance,
    location: route.query.location || '',
    status: 'none',
  })
  const orderByDate = ref(route.query.volgorde === 'oplopend' ? 'ASC' : 'DESC')

  // Resolve the location from the url before the first search, so that search already includes the distance.
  // `useAsyncData()` does this on the server for a full page request, and in the browser on client side navigation.
  const { data: resolvedLocation } = await useAsyncData(`animal-overview;location;${orderByLocation.value.location};${orderByLocation.value.distance}`, () =>
    locationSearchStore.resolve(orderByLocation.value),
  )
  if (resolvedLocation.value) {
    orderByLocation.value = resolvedLocation.value
  }

  // Only the latest lookup may update the location, so a slower lookup of an earlier input (e.g. 'Rotte' while typing
  // 'Rotterdam') cannot overwrite a newer one
  let latestLookup = 0
  const resolveLatest = async (input: Record<string, any>) => {
    const lookup = ++latestLookup
    const value = await locationSearchStore.resolve(input)
    if (lookup === latestLookup) {
      orderByLocation.value = value
    }
  }

  const setLocation = async (location: string) => {
    await resolveLatest({ ...orderByLocation.value, location })
  }

  const setDistance = async (distance: string) => {
    await resolveLatest({ ...orderByLocation.value, distance })
  }

  return { orderByDate, orderByLocation, setDistance, setLocation }
}

async function setupSearch(limit: number, onlyWhenAvailable: any, enabledFacetConfigs: any[], animalCategory: any, animalType: any, orderByLocation: any, orderByDate: any) {
  const pageNumber = ref(route.query.page ? Number.parseInt(route.query.page) : 1)
  const isReady = ref(undefined)

  // todo: split input props into separate reactive variables
  const input = ref({
    ...Object.fromEntries(enabledFacetConfigs.map((config) => [config.handle, toStringArray(route.query[config.uriName])])),
  })

  const variables = computed(() => ({
    // Always supply a site attribute, though it might be overwritten though the `enabledFacetConfigs`.
    // Hence start with `site: []`, and follow with `...Object.fromEntries(enabledFacetConfigs`
    site: [],
    ...Object.fromEntries(enabledFacetConfigs.map((config) => [config.handle, config.toSearchFacetArgument(input.value[config.handle])])),
    animalAvailability: onlyWhenAvailable.value ? ['available'] : [],
    animalCategory,
    animalType,
    limit,
    offset: (pageNumber.value - 1) * limit,
    orderBy:
      orderByLocation.value.status === 'ok'
        ? [`${animalType === 'available' ? 'map' : 'animalMap'} POINT(${orderByLocation.value.lng} ${orderByLocation.value.lat}) ASC`]
        : [`postDate ${orderByDate.value}`],
    distance: orderByLocation.value.status === 'ok' ? `POINT(${orderByLocation.value.lng} ${orderByLocation.value.lat}) ${orderByLocation.value.distance}` : undefined,
  }))

  const entries = ref([])
  const count = ref(0)
  const search = async (variables_: any): Promise<{ count: number; entries: any[] }> => {
    const key = `animal-overview;search;${JSON.stringify(variables_)}`
    const ttl = 3
    const fetcher = async () => {
      const { elasticSearch } = await graphqlFetch({
        token: 'elasticsearch',
        query: `
          query setupSearch($orderBy: [String], $distance: String, $animalCategory: [String], $animalType: [String], $animalAvailability: [String], $limit: Int, $offset: Int, $site: [String], ${enabledFacetConfigs
            .filter((facet) => facet.handle !== 'site')
            .map((facet) => `$${facet.handle}: [String]`)
            .join()}) {
            elasticSearch(
              orderBy: $orderBy
              ${animalType === 'available' ? 'mapDistance' : 'animalMapDistance'}: $distance
              section: "animal"
              animalCategoryFacet: $animalCategory
              animalTypeFacet: $animalType
              animalAvailabilityFacet: $animalAvailability
              site: $site
              ${enabledFacetConfigs
                .filter((facet) => facet.handle !== 'site')
                .map((facet) => `${facet.facetName}: $${facet.handle}`)
                .join(`\n`)}
            ) {
              count
              hits(limit: $limit, offset: $offset) {
                id
              }
            }
          }`,
        variables: variables_,
        params: Object.fromEntries(Object.entries({ animalCategory: variables_.animalCategory, animalType: variables_.animalType }).filter(([_key, value]) => !!value)),
      })
      return { count: elasticSearch.count, entries: elasticSearch.hits }
    }
    return await cacheStore.fetch({ key, ttl, fetcher, fallback: { count: 0, entries: [] } })
  }

  // Only the latest request may update the results, so a slower response of an earlier request (e.g. while typing a
  // location) cannot overwrite the results of a newer one
  let latestRequest = 0
  const update = async (variables_: any) => {
    const request = ++latestRequest
    isReady.value = false
    const result = await search(variables_)
    if (request !== latestRequest) {
      return
    }
    count.value = result.count
    entries.value = result.entries
    isReady.value = true
  }

  // Register the watcher before the `await` below, because the component instance is no longer active after it
  watch(variables, update)

  // Run the first search on the server for a full page request, and in the browser on client side navigation
  const { data: initialResult } = await useAsyncData(`animal-overview;search;${JSON.stringify(variables.value)}`, () => search(variables.value))
  if (initialResult.value) {
    count.value = initialResult.value.count
    entries.value = initialResult.value.entries
    isReady.value = true
  }

  return {
    count,
    entries,
    input,
    isReady,
    orderByDate,
    orderByLocation,
    pageNumber,
  }
}

const { domain } = setupSiteConfig()
const { animalCategory, animalType } = setupFilters(props.page)
const enabledFacetConfigs = setupAnimalFacetConfigs(animalCategory, animalType)
const facetValueToLabel = setupFacetValueToLabel()
const { onlyWhenAvailable } = setupOnlyWhenAvailable()
const { facets } = setupFacets(onlyWhenAvailable, enabledFacetConfigs, animalCategory, animalType)
const { orderByDate, orderByLocation, setDistance, setLocation } = await setupLocation()
const { count, entries, input, isReady, pageNumber } = await setupSearch(
  props.limit,
  onlyWhenAvailable,
  enabledFacetConfigs,
  animalCategory,
  animalType,
  orderByLocation,
  orderByDate,
)
const facetsOpened = ref(Object.fromEntries(enabledFacetConfigs.map((config) => [config.handle, input[config.handle]?.length > 0])))
const mobileSearchFilterOpened = ref(false)
const updateOrderByDate = (val: string) => {
  orderByDate.value = val
}

setupDynamicUrl(orderByDate, (value) => ({ volgorde: value === 'DESC' ? undefined : 'oplopend' }))
setupDynamicUrl(input, (value) => ({
  ...Object.fromEntries(enabledFacetConfigs.map((config) => [config.uriName, value[config.handle]])),
}))
setupDynamicUrl(pageNumber, (value) => ({ page: value === 1 ? undefined : value }))
setupDynamicUrl(orderByLocation, (value) => ({
  distance: value.distance === fallbackDistance ? undefined : value.distance,
  location: value.location ? value.location : undefined,
}))
setupDynamicUrl(onlyWhenAvailable, (value) => ({ animalAvailability: value ? 'available' : undefined }))

const activeFacets = computed(() =>
  enabledFacetConfigs
    .map((config) => ({
      label: config.label,
      active: input.value[config.handle].map((value) => ({
        handle: config.handle,
        label: facetValueToLabel(config, value),
        value,
      })),
    }))
    .filter((facet) => facet.active.length),
)

function deactivateFacet({ handle, value }: { handle: string; value: string }) {
  input.value[handle].splice(input.value[handle].indexOf(value), 1)
}

const searchProfile = computed(() => {
  return {
    // Every profile has a category is hard
    category: props.page.animalCategory,

    // Optionally a city, distance, lat, lng, and location is specified
    // Note that location and distance corresponds with the user input (also used in the URL), i.e. search input is 'rotterd'
    // while city corresponds with what we show in the account, i.e. 'Results around Rotterdam'
    // while lat and lng corresponds with what we send to Elasticsearch for the GEO distance
    city: orderByLocation.value?.city,
    distance: orderByLocation.value?.distance === fallbackDistance ? undefined : orderByLocation.value?.distance,
    lat: orderByLocation.value?.lat,
    lng: orderByLocation.value?.lng,
    location: orderByLocation.value?.location || undefined,

    // Optionally animalAvailability when onlyWhenAvailable is true
    animalAvailability: onlyWhenAvailable.value ? 'available' : undefined,

    // All other values are facets
    ...Object.fromEntries(enabledFacetConfigs.map((config) => [config.uriName, input.value[config.handle]]).filter(([_handle, value]) => value.length)),
  }
})
const hasSearchProfile = computed(() => searchProfilesStore.hasProfile(searchProfile.value))
const toggleSearchProfile = () => searchProfilesStore.toggleProfile({ profile: searchProfile.value })

const filtersOpened = ref(false)
const toggleFilters = () => {
  filtersOpened.value = !filtersOpened.value

  const bodyElement = document.querySelector('body')
  if (filtersOpened.value) {
    bodyElement.classList.add('i-animal-overview-body-filters-opened')
  } else {
    bodyElement.classList.remove('i-animal-overview-body-filters-opened')
  }
}

const ctas = ref(null)

watchEffect(async () => {
  const site = unref(route).meta.site

  if (site === 'mijnDierIsZoek') {
    ctas.value = null
  } else {
    ctas.value = await ctaStore.fetch(animalCategory)
  }
})

onUnmounted(() => {
  const bodyElement = document.querySelector('body')
  bodyElement.classList.remove('i-animal-overview-body-filters-opened')
})

const { t_, tc_ } = setupTranslationPrefixer('AnimalOverview', animalType)
</script>

<style lang="scss" scoped>
.filters--opened {
  padding-top: 74px;
  z-index: 666;
}

.i-animal-overview-body-filters-opened {
  overflow: hidden;

  @screen md {
    overflow: auto;
  }
}

.formulate-input.c-animal-overview-formulate-container-checkbox {
  @apply mb-3;

  @screen lg {
    @apply mb-5;
  }
}

:deep(.c-animal-overview-label-available-animals) {
  text-underline-offset: theme('spacing.px');
  text-decoration-thickness: 2px;
  text-decoration-color: theme('colors.app.green');

  @screen xl {
    text-underline-offset: theme('spacing.1');
  }
}

.facet-group-smooth-reflow-container {
  .formkit-fieldset .formkit-legend {
    @apply sr-only;
  }
}

.formulate-input-element--checkbox input[type='checkbox']:focus + .formulate-input-element-decorator {
  border: 2px solid theme('colors.app.green');
}
</style>
